"use client";

import { useTranslations } from "next-intl";
import { useQueryState } from "nuqs";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormState } from "react-hook-form";

import { ProductType } from "@/constants";
import { useSubmitOnce } from "@/hooks/useSubmitOnce";
import { logError } from "@/lib/logError";
import { reportSilentFailure } from "@/lib/reportSilentFailure";
import { toast } from "@/lib/toast";
import { useCustomerAccount } from "@/providers";
import { useRecipientDraftStore } from "@/stores/recipientDraft/useRecipientDraftStore";
import {
  type BeneficiaryFieldError,
  resolveBeneficiaryFieldError,
} from "@/utils/resolveBeneficiaryFieldError";
import {
  type BeneficiaryTemplatesVerificationResult,
  verifyBeneficiaryTemplates,
} from "@/utils/verifyBeneficiaryTemplates";
import {
  useCreateBeneficiaryMutation,
  useCreateBlockchainBeneficiaryMutation,
  useGetBeneficiaryTemplatesQuery,
} from "@generated/redux/services/generated/api";
import {
  AccountBankAccount,
  AccountBeneficiaryAddress,
  BeneficiaryTemplate,
  CopCheckResponse,
  FilteredBeneficiaryDetail,
  FilteredResolvedBlockchainBeneficiary,
  LegalEntityType,
  WalletType,
} from "@generated/types";

import {
  EMPTY_BLOCKCHAIN_DETAILS,
  EMPTY_FIAT_DETAILS,
  RecipientFormsProvider,
  useRecipientForms,
} from "./RecipientFormsProvider";
import {
  type BlockchainTravelRuleAddress,
  buildBlockchainCreateRequest,
  buildFiatCreateRequest,
} from "./buildCreateRequests";
import {
  BeneficiaryFormStep,
  NO_STEP_MARKERS,
  readStepQuery,
  type RecipientStepMarkers,
  resolveEffectiveStep,
  resolveRecipientStepTransition,
  stepQueryParser,
} from "./recipientStepGuard";
import { reportBeneficiaryFailure } from "./resolveBeneficiaryFailureReport";

export { BeneficiaryFormStep } from "./recipientStepGuard";

type BeneficiaryProviderValue = {
  // Form state
  destinationType: ProductType;
  setDestinationType: (type: ProductType) => void;
  beneficiaryType: LegalEntityType | null;
  setBeneficiaryType: (type: LegalEntityType) => void;
  currency: string;
  setCurrency: (currency: string) => void;
  country: string;
  setCountry: (country: string) => void;
  name: string;
  setName: (name: string) => void;
  bankAccount: Partial<AccountBankAccount>;
  setBankAccount: (bankAccount: Partial<AccountBankAccount>) => void;
  /**
   * What the account step's submit saves: the bank account and the template
   * it was built from, together, so the create's operation code always comes
   * from the template the user actually filled in.
   */
  saveBankAccount: (
    bankAccount: Partial<AccountBankAccount>,
    template: BeneficiaryTemplate | null
  ) => void;
  updateBankAccountField: (field: string, value: string) => void;
  /**
   * Field-level validation error returned by the BFF on createBeneficiary
   * (e.g. an invalid BIC/IBAN). Set when the create is rejected so the account
   * step highlights the offending field; cleared on the next submit.
   */
  bankAccountFieldError: BeneficiaryFieldError | null;
  clearBankAccountFieldError: () => void;

  // Blockchain-specific state
  selectedAsset: string;
  /** Also forgets the resolved asset UUID: it belonged to the old pair. */
  setSelectedAsset: (asset: string) => void;
  selectedAssetUuid: string;
  setSelectedAssetUuid: (uuid: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedNetwork: any;
  /** Also forgets the resolved asset UUID: it belonged to the old pair. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSelectedNetwork: (network: any) => void;
  /** The travel-rule identity from the blockchain address step. */
  blockchainRecipientAddress: BlockchainTravelRuleAddress;
  setBlockchainRecipientAddress: (
    address: Partial<BlockchainTravelRuleAddress>
  ) => void;

  // Payment reason / purpose code
  purposeCode: string;
  setPurposeCode: (code: string) => void;

  // CoP check result
  copCheckResult: CopCheckResponse | null;
  setCopCheckResult: (result: CopCheckResponse | null) => void;
  copUserAction: string;
  setCopUserAction: (action: string) => void;

  // Beneficiary templates
  beneficiaryTemplates?: BeneficiaryTemplate[];
  selectedTemplate: BeneficiaryTemplate | null;
  setSelectedTemplate: (template: BeneficiaryTemplate | null) => void;
  /**
   * The template picker's position on the account step. Lives here, not in
   * the step, so a Back or a BFF field-error bounce comes back to the template
   * the user filled in (and the rejected field is one that is rendered).
   */
  selectedTemplateIndex: number;
  setSelectedTemplateIndex: (index: number) => void;
  /** The template the saved bank account was built from (see saveBankAccount). */
  savedTemplate: BeneficiaryTemplate | null;
  isFetchingTemplates: boolean;
  isErrorTemplates: boolean;
  refetchTemplates: () => {
    unwrap: () => Promise<{ data?: readonly unknown[] }>;
  };
  hasTemplatesForCorridor: boolean;
  verifyTemplatesAvailable: () => Promise<BeneficiaryTemplatesVerificationResult>;

  // Actions
  createBeneficiary: (data: {
    reference?: string;
    nickname?: string;
    address?: Partial<AccountBeneficiaryAddress>;
  }) => Promise<FilteredBeneficiaryDetail | null>;
  createBlockchainBeneficiary: (data: {
    address: string;
    walletType: WalletType;
    vaspId?: string;
    vaspName?: string;
    vaspAdditionalInfo?: string;
    nickname?: string;
  }) => Promise<FilteredResolvedBlockchainBeneficiary | null>;
  isLoadingCreate: boolean;
  isLoadingCreateBlockchain: boolean;
  /**
   * Call after a create succeeds, before navigating to the new recipient.
   * True means "navigate": the wizard is still mounted and still on the
   * account the entry was pinned to. It also drops the unload warning, so the
   * page stays eligible for the back-forward cache. False means the payee
   * exists but the user has moved on (or switched account): confirm with a
   * toast and do not pull them back.
   */
  completeCreate: () => boolean;

  // Navigation
  /**
   * The step the wizard shows: `?step` if the commit markers allow it,
   * otherwise the highest reachable step. Computed during render
   * (recipientStepGuard.ts), so a locked step never renders, even for a frame.
   */
  effectiveStep: BeneficiaryFormStep;
  /**
   * True while `step` is the step on screen. Every submit / Continue handler
   * returns early otherwise: the outgoing step stays mounted for the ~300ms
   * exit, and an event that reaches it must not act on the entry.
   */
  isActiveStep: (step: BeneficiaryFormStep) => boolean;
  /**
   * Forward moves only (shallow history push). Deliberately cannot express
   * `Destination`: every arrival there restarts the entry, which the provider
   * now enforces itself in a layout effect for any route in — an in-app exit,
   * browser Back or a URL edit. The type keeps call sites honest; the provider
   * state is what guarantees it.
   */
  setCurrentStep: (
    step: BeneficiaryFormStep.Account | BeneficiaryFormStep.Details
  ) => void;
  /** Destination Continue passed its check (template check / resolved asset). */
  commitDestination: () => void;
  /** A CoP modal handler, or the blockchain address Continue, accepted the step. */
  commitAccount: () => void;
  /** Details → Account. The step guard walks history back one entry. */
  goBackToAccount: () => void;
  /**
   * The only in-app way back to the Destination step. Backing out to
   * Destination is a "restart this recipient" gesture, so it drops everything
   * entered downstream of it; otherwise one payee's bank details, home address
   * and reference pre-fill the next payee without the user ever leaving the
   * wizard. Arriving at Destination by browser Back runs the same clear.
   */
  restartRecipientEntry: () => void;
  canProceedToAccountDetails: boolean;

  // Pinned account
  /**
   * The live customer account is no longer the one this entry started on
   * (another tab, or a /self reselect). Continue and Create are blocked; the
   * draft is kept so the user can switch back.
   */
  isAccountMismatch: boolean;
  /** The name of the account the entry started on, for the mismatch alert. */
  pinnedAccountName: string | null;
  /**
   * Mismatch alert's way out: an empty entry pinned to the live account.
   * Unlike restartRecipientEntry it also drops the Destination selection
   * (currency, country, asset, network): the live account may not offer the
   * corridor the entry was started on, so the user picks it again.
   */
  startAgainOnLiveAccount: () => void;

  // Helpers
  resetForm: () => void;
};

const BeneficiaryContext = createContext<BeneficiaryProviderValue | undefined>(
  undefined
);

/**
 * The recipient wizard's state. Mounted by `recipient/new/layout.tsx` and
 * nowhere higher, so leaving the route drops the draft; nothing about the draft
 * is ever written to sessionStorage or localStorage.
 */
export function BeneficiaryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <RecipientFormsProvider>
      <BeneficiaryStateProvider>{children}</BeneficiaryStateProvider>
    </RecipientFormsProvider>
  );
}

/**
 * Publishes whether the details forms are dirty. A leaf of its own so the
 * formState subscription re-renders only this component (on a dirty flip),
 * never the provider or the layout's sticky header.
 */
function DetailsDirtyWatcher({
  onChange,
}: {
  onChange: (isDirty: boolean) => void;
}) {
  const { fiatDetailsForm, blockchainDetailsForm } = useRecipientForms();
  const { isDirty: isFiatDirty } = useFormState({
    control: fiatDetailsForm.control,
  });
  const { isDirty: isBlockchainDirty } = useFormState({
    control: blockchainDetailsForm.control,
  });
  const isDirty = isFiatDirty || isBlockchainDirty;

  useEffect(() => {
    onChange(isDirty);
  }, [isDirty, onChange]);

  return null;
}

function BeneficiaryStateProvider({ children }: { children: React.ReactNode }) {
  const { customerAccount } = useCustomerAccount();
  const { fiatDetailsForm, blockchainDetailsForm } = useRecipientForms();
  const tRecipients = useTranslations("recipients");
  const tToast = useTranslations("recipients.toast");

  // ?step: one parser tells a step, junk and "no step" apart (junk is asked
  // for as Destination, flagged invalid so the URL gets corrected).
  const [stepQuery, setStepQuery] = useQueryState("step", stepQueryParser);
  const { requested: requestedStep, requestedIsValid } =
    readStepQuery(stepQuery);

  // Read and write destination type from/to query param
  const [typeQuery, setTypeQuery] = useQueryState("type");

  const [stepMarkers, setStepMarkers] =
    useState<RecipientStepMarkers>(NO_STEP_MARKERS);

  // The step guard runs during render: a locked ?step is never rendered.
  const effectiveStep = resolveEffectiveStep(requestedStep, stepMarkers);

  // The step on screen as event handlers see it. Updated synchronously by
  // every in-app transition (so a second event in the same tick already sees
  // the move) and reconciled with the guard in the layout effect below.
  const activeStepRef = useRef<BeneficiaryFormStep>(effectiveStep);
  const isActiveStep = useCallback(
    (step: BeneficiaryFormStep) => activeStepRef.current === step,
    []
  );

  // Initialize destination type from query param or default to "currencies"
  const initialDestinationType: ProductType =
    typeQuery === "digital-assets" ? "digital-assets" : "currencies";

  const [destinationType, setDestinationTypeState] = useState<ProductType>(
    initialDestinationType
  );

  // Wrapper to sync destination type with URL query param
  const setDestinationType = useCallback(
    (type: ProductType) => {
      setDestinationTypeState(type);
      void setTypeQuery(type);
    },
    [setTypeQuery]
  );
  const [beneficiaryType, setBeneficiaryType] =
    useState<LegalEntityType | null>(LegalEntityType.Corporation);
  const [currency, setCurrency] = useState("");
  const [country, setCountry] = useState("");
  const [name, setName] = useState("");
  const [bankAccount, setBankAccount] = useState<Partial<AccountBankAccount>>(
    {}
  );
  const [selectedTemplate, setSelectedTemplate] =
    useState<BeneficiaryTemplate | null>(null);
  const [pickedTemplateIndex, setSelectedTemplateIndex] = useState(0);
  const [savedTemplate, setSavedTemplate] =
    useState<BeneficiaryTemplate | null>(null);

  // Blockchain-specific state
  const [selectedAsset, setSelectedAssetState] = useState("");
  const [selectedAssetUuid, setSelectedAssetUuid] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedNetwork, setSelectedNetworkState] = useState<any>(null);
  const [blockchainRecipientAddress, setBlockchainRecipientAddress] =
    useState<BlockchainTravelRuleAddress>({});
  const [copCheckResult, setCopCheckResult] = useState<CopCheckResponse | null>(
    null
  );
  const [copUserAction, setCopUserAction] = useState<string>("");
  const [purposeCode, setPurposeCode] = useState<string>("");
  // Bank-detail field rejected by the BFF on the last create attempt (BIC/IBAN
  // constraint violation). Drives the account-step field highlight.
  const [bankAccountFieldError, setBankAccountFieldError] =
    useState<BeneficiaryFieldError | null>(null);
  const clearBankAccountFieldError = useCallback(
    () => setBankAccountFieldError(null),
    []
  );

  // A resolved virtual-asset UUID belongs to one asset + network pair; any
  // change to either makes it stale, so the Destination step must resolve
  // again before Continue.
  const setSelectedAsset = useCallback((asset: string) => {
    setSelectedAssetState(asset);
    setSelectedAssetUuid("");
  }, []);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setSelectedNetwork = useCallback((network: any) => {
    setSelectedNetworkState(network);
    setSelectedAssetUuid("");
  }, []);

  const saveBankAccount = useCallback(
    (
      nextBankAccount: Partial<AccountBankAccount>,
      template: BeneficiaryTemplate | null
    ) => {
      setBankAccount(nextBankAccount);
      setSavedTemplate(template);
    },
    []
  );

  // --- Pinned account -------------------------------------------------------
  // The first customer account that resolves in this mount is the one the
  // entry belongs to, and the only id either create sends.
  const liveAccountId = customerAccount?.customerAccount?.id ?? null;
  const liveAccountName = customerAccount?.customerAccount?.name ?? null;
  const [pinnedAccount, setPinnedAccount] = useState<{
    id: string;
    name: string | null;
  } | null>(null);
  if (pinnedAccount === null && liveAccountId !== null) {
    // Adjusting state during render (not in an effect) so the very first
    // committed render is already pinned.
    setPinnedAccount({ id: liveAccountId, name: liveAccountName });
  }
  const isAccountMismatch =
    pinnedAccount !== null &&
    liveAccountId !== null &&
    liveAccountId !== pinnedAccount.id;

  // Read by the create continuations, which resume after an await and must
  // see the account as it is then, not as it was when they started.
  const liveAccountIdRef = useRef(liveAccountId);
  const pinnedAccountIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(false);
  useLayoutEffect(() => {
    liveAccountIdRef.current = liveAccountId;
    pinnedAccountIdRef.current = pinnedAccount?.id ?? null;
  }, [liveAccountId, pinnedAccount]);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // API hooks
  const {
    data: templatesData,
    isFetching: isFetchingTemplates,
    isError: isErrorTemplates,
    refetch: refetchTemplates,
  } = useGetBeneficiaryTemplatesQuery(
    {
      filterCurrency: currency ? [currency] : undefined,
      filterCountry: country ? [country] : undefined,
    },
    {
      skip: !currency || !country,
    }
  );

  const [createBeneficiaryMutation, { isLoading: isLoadingCreate }] =
    useCreateBeneficiaryMutation();

  const [
    createBlockchainBeneficiaryMutation,
    { isLoading: isLoadingCreateBlockchain },
  ] = useCreateBlockchainBeneficiaryMutation();

  // Filter templates client-side to ensure only matching beneficiaryType templates are included
  const beneficiaryTemplates = useMemo(() => {
    const templates = templatesData?.data;
    if (!templates || !beneficiaryType) return templates;
    return templates.filter(
      (template) =>
        (template.beneficiaryType as string) === (beneficiaryType as string)
    );
  }, [templatesData?.data, beneficiaryType]);

  // The picker's position indexes the type-filtered list above. A type flip
  // (the radio, or a CoP accept-suggestion) can shorten that list under it,
  // so a position past the end falls back to the first template: the account
  // step always renders one, and Continue never checks an empty account.
  const selectedTemplateIndex =
    beneficiaryTemplates && pickedTemplateIndex >= beneficiaryTemplates.length
      ? 0
      : pickedTemplateIndex;

  // Unfiltered "does this corridor have any templates at all?" signal.
  // Distinct from `beneficiaryTemplates.length > 0` because the filter above
  // can yield an empty list when templates EXIST for the corridor but not for
  // the currently-selected beneficiaryType (user can recover by toggling).
  const hasTemplatesForCorridor =
    !!templatesData?.data && templatesData.data.length > 0;

  const verifyTemplatesAvailable = useCallback(
    () =>
      verifyBeneficiaryTemplates(
        refetchTemplates as () => {
          unwrap: () => Promise<{ data?: readonly unknown[] }>;
        }
      ),
    [refetchTemplates]
  );

  // Helper to update nested bank account fields
  const updateBankAccountField = useCallback((field: string, value: string) => {
    setBankAccount((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Validation helpers
  const canProceedToAccountDetails =
    !!beneficiaryType && !!currency && !!country;

  // Submit-once guards for the two creates (hooks/useSubmitOnce.ts): a second
  // Create click in the same tick would otherwise create the recipient twice.
  // A success stays latched (the wizard navigates away, or the payee already
  // exists); every failure releases so the user can retry; a restart re-arms,
  // because it begins a different recipient. The incomplete-state returns run
  // before begin() and never engage the guard.
  const createGuard = useSubmitOnce();
  const createBlockchainGuard = useSubmitOnce();

  // --- Step transitions -----------------------------------------------------

  // Drop everything the user entered downstream of the Destination step.
  //
  // Deliberately KEPT: destinationType, currency, selectedAsset,
  // selectedNetwork — the Destination step's own selection, which the user is
  // about to see and change. selectedAssetUuid is cleared: Destination
  // Continue resolves it again. beneficiaryType is also kept: it is
  // re-collected by a visible radio on the very next step and carries no PII,
  // and resetting it would ripple into the corridor's template filtering for
  // no user benefit.
  //
  // `country` is not in either list because it is not ours to decide: the fiat
  // Destination step clears it on mount whenever a currency is set
  // (fiat-recipient-destination.tsx), so the user always re-picks it.
  //
  // Idempotent: it runs for an in-app restart and again when the step guard
  // sees the arrival at Destination.
  const clearEntry = useCallback(() => {
    setStepMarkers(NO_STEP_MARKERS);
    setBankAccount({});
    setName("");
    setSelectedTemplate(null);
    setSelectedTemplateIndex(0);
    setSavedTemplate(null);
    setBankAccountFieldError(null);
    setPurposeCode("");
    setBlockchainRecipientAddress({});
    setSelectedAssetUuid("");
    // A name-verification result belongs to the payee it was run against; it
    // must never ride along into the next recipient's create payload.
    setCopCheckResult(null);
    setCopUserAction("");
    // Named constants, never reset() / reset({}): those restore the stored
    // default values rather than emptying the form.
    fiatDetailsForm.reset(EMPTY_FIAT_DETAILS);
    blockchainDetailsForm.reset(EMPTY_BLOCKCHAIN_DETAILS);
    createGuard.release();
    createBlockchainGuard.release();
  }, [
    setStepMarkers,
    fiatDetailsForm,
    blockchainDetailsForm,
    createGuard,
    createBlockchainGuard,
  ]);

  const restartRecipientEntry = useCallback(() => {
    activeStepRef.current = BeneficiaryFormStep.Destination;
    clearEntry();
  }, [clearEntry]);

  const setCurrentStep = useCallback(
    (step: BeneficiaryFormStep.Account | BeneficiaryFormStep.Details) => {
      activeStepRef.current = step;
      void setStepQuery(step, { history: "push" });
    },
    [setStepQuery]
  );

  const commitDestination = useCallback(() => {
    setStepMarkers((markers) => ({ ...markers, destinationCommitted: true }));
  }, [setStepMarkers]);

  const commitAccount = useCallback(() => {
    setStepMarkers((markers) => ({ ...markers, accountCommitted: true }));
  }, [setStepMarkers]);

  const goBackToAccount = useCallback(() => {
    activeStepRef.current = BeneficiaryFormStep.Account;
    setStepMarkers((markers) => ({ ...markers, accountCommitted: false }));
  }, [setStepMarkers]);

  // The step guard's side effects. Layout effect, so they land before paint:
  // - arriving at Destination from a later step restarts the entry, whatever
  //   the route in (an in-app exit, browser Back, a URL edit);
  // - arriving at Account voids the CoP result and the account commit, so
  //   browser Forward cannot skip a fresh CoP check;
  // - a ?step the markers do not allow is corrected: replaced on a fresh mount
  //   (or for junk), walked back one entry inside a mount. In-app Back moves
  //   (Details → Account, Account → Destination) clear a marker and let this
  //   walk-back do the navigating, so history and state never disagree.
  //
  // `window.history.back()` is what the app router's `router.back()` calls; it
  // is used directly so this provider needs no app-router context.
  const previousStepRef = useRef<BeneficiaryFormStep | null>(null);
  const correctedLandingRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const transition = resolveRecipientStepTransition({
      requested: requestedStep,
      requestedIsValid,
      markers: stepMarkers,
      previous: previousStepRef.current,
    });
    previousStepRef.current = transition.effectiveStep;
    activeStepRef.current = transition.effectiveStep;

    if (transition.restart) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- the restart is a reaction to the step the URL arrived at (browser Back, URL edit), which only an effect sees; layout timing keeps it ahead of paint
      clearEntry();
    } else if (transition.enterAccount) {
      setStepMarkers((markers) =>
        markers.accountCommitted
          ? { ...markers, accountCommitted: false }
          : markers
      );
      setCopCheckResult(null);
      setCopUserAction("");
    }

    if (transition.correction === "none") {
      correctedLandingRef.current = null;
      return;
    }
    // One correction per URL landing: the URL updates asynchronously, and a
    // re-render before it does must not walk back a second entry. (Every junk
    // value shares one key; a junk landing is always replaced by a valid one,
    // which resets the key, before another junk value can arrive.)
    const landing = stepQuery;
    if (correctedLandingRef.current === landing) return;
    correctedLandingRef.current = landing;

    if (transition.correction === "replace") {
      void setStepQuery(
        transition.effectiveStep === BeneficiaryFormStep.Destination
          ? null
          : transition.effectiveStep,
        { history: "replace" }
      );
    } else {
      window.history.back();
    }
  }, [
    requestedStep,
    requestedIsValid,
    stepQuery,
    stepMarkers,
    clearEntry,
    setStepQuery,
  ]);

  // --- Creates --------------------------------------------------------------

  // Create fiat beneficiary
  const createBeneficiary = useCallback(
    async ({
      reference,
      nickname,
      address,
    }: {
      reference?: string;
      nickname?: string;
      address?: Partial<AccountBeneficiaryAddress>;
    }): Promise<FilteredBeneficiaryDetail | null> => {
      if (!pinnedAccount || !beneficiaryType || !currency) {
        // Only reachable from the Create button, which the wizard does not
        // render until all three have been collected — so landing here means
        // provider state was lost (remount, account switch), not user error.
        // The toast unblocks the user; this makes the state loss visible.
        reportSilentFailure({
          message: "[Recipient] Creation blocked — wizard state incomplete",
          level: "warning",
          fingerprint: "recipient-create-state-incomplete",
          tags: {
            destination: "fiat",
            missingCustomerAccount: String(!pinnedAccount),
            missingBeneficiaryType: String(!beneficiaryType),
            missingCurrency: String(!currency),
          },
        });
        toast.error(tToast("missingRequiredInformation"));
        return null;
      }

      // The live account moved away from the one this entry started on. The
      // mismatch alert already says so and Create is disabled; this refuses a
      // create that got past both, so nothing reaches the wrong account.
      if (liveAccountIdRef.current !== pinnedAccount.id) return null;

      // A create from the same click is already in flight: its result drives
      // the wizard, so this one does nothing.
      if (!createGuard.begin()) return null;
      try {
        const result = await createBeneficiaryMutation({
          createBeneficiaryRequest: buildFiatCreateRequest({
            customerAccountId: pinnedAccount.id,
            currency,
            country,
            beneficiaryType,
            name,
            bankAccount,
            savedTemplate,
            purposeCode,
            copCheck: copCheckResult
              ? { result: copCheckResult, userAction: copUserAction }
              : null,
            today: new Date().toISOString().split("T")[0] ?? "",
            details: { reference, nickname, address },
          }),
        }).unwrap();

        return result as unknown as FilteredBeneficiaryDetail;
      } catch (error) {
        createGuard.release();
        // The accounts service validates bank details (BIC pattern, IBAN
        // format, …) and rejects malformed input with a 400
        // constraint-violation-exception. That rejection is correct and stays
        // visible in Sentry (it's in ALWAYS_BUG_EXCEPTION_TYPES — see
        // redux/services/sentryErrorCapture.ts); here we recover the UX. When
        // the failure maps to a known field, route the user one step back to
        // the bank-details form and highlight that field with a readable
        // message instead of surfacing the raw regex string.
        const fieldError = resolveBeneficiaryFieldError(error);

        // Resolved once, for BOTH branches, so `hasFieldError` is the thing
        // that actually decides — rather than a parameter the call site pins
        // to `false` while an early return does the real work. Returns null
        // whenever something else already reported (see the resolver).
        reportBeneficiaryFailure(error, {
          destination: "fiat",
          hasFieldError: !!fieldError,
          hasCopVerification: !!copCheckResult,
          endpointName: "createBeneficiary",
        });

        if (fieldError) {
          logError("Recipient creation rejected a bank-account field", error, {
            flow: "createBeneficiary",
            field: fieldError.field,
          });
          setBankAccountFieldError(fieldError);
          // Bounce only if the user is still on Details. If they already
          // left, the recorded error highlights the field when they get back
          // to the account step.
          if (activeStepRef.current === BeneficiaryFormStep.Details) {
            goBackToAccount();
          }
          toast.error(tRecipients(fieldError.messageKey));
          return null;
        }
        // Any other failure: generic translated message (never the raw
        // backend body, per the error-handling rules).
        //
        // This branch is the app admitting it cannot explain the failure, and
        // it is a hard dead end — the only thing left for the user is to press
        // the same button again. Two tiers, with different jobs:
        //
        //  - `logError` ALWAYS runs: it puts the status + upstream
        //    `ExceptionDetail` into the Mixpanel replay console (as JSON, not
        //    "[object Object]"), fires a queryable "Error Logged" event, and
        //    routes a plain thrown `Error` to Sentry.
        //  - `reportSilentFailure` runs only when nothing else reported —
        //    `resolveBeneficiaryFailureReport` asks `captureRtkQueryError`'s
        //    own predicate rather than guessing, so a 5xx (already an issue)
        //    doesn't become two. What's left is the class both trackers drop:
        //    a structured 4xx classified as "user input the UI explains",
        //    surfaced through a toast that explains nothing. 2026-09-08: one
        //    globalconnect customer hit it four times over two minutes and the
        //    only trace anywhere was a Mixpanel replay console line.
        logError("Recipient creation failed", error, {
          flow: "createBeneficiary",
          hasCopVerification: !!copCheckResult,
        });
        toast.error(tRecipients("error.failedToCreateRecipient"));
        return null;
      }
    },
    [
      createGuard,
      pinnedAccount,
      beneficiaryType,
      currency,
      name,
      bankAccount,
      country,
      savedTemplate,
      copCheckResult,
      copUserAction,
      purposeCode,
      createBeneficiaryMutation,
      goBackToAccount,
      tRecipients,
      tToast,
    ]
  );

  // Create blockchain beneficiary
  const createBlockchainBeneficiary = useCallback(
    async (details: {
      address: string;
      walletType: WalletType;
      vaspId?: string;
      vaspName?: string;
      vaspAdditionalInfo?: string;
      nickname?: string;
    }): Promise<FilteredResolvedBlockchainBeneficiary | null> => {
      // The empty-UUID check is the last line behind the Destination step,
      // which refuses to advance without a resolved asset.
      if (!pinnedAccount || !selectedAssetUuid || !details.address) {
        // Blockchain twin of the fiat guard above — same reasoning.
        reportSilentFailure({
          message: "[Recipient] Creation blocked — wizard state incomplete",
          level: "warning",
          fingerprint: "recipient-create-state-incomplete",
          tags: {
            destination: "blockchain",
            missingCustomerAccount: String(!pinnedAccount),
            missingAsset: String(!selectedAssetUuid),
            missingAddress: String(!details.address),
          },
        });
        toast.error(tToast("missingRequiredInformation"));
        return null;
      }

      // Same pinned-account refusal as the fiat create.
      if (liveAccountIdRef.current !== pinnedAccount.id) return null;

      // Same-click duplicate — see the fiat create above.
      if (!createBlockchainGuard.begin()) return null;

      try {
        const result = await createBlockchainBeneficiaryMutation({
          body: buildBlockchainCreateRequest({
            customerAccountId: pinnedAccount.id,
            assetUuid: selectedAssetUuid,
            beneficiaryType,
            travelRuleAddress: blockchainRecipientAddress,
            details,
          }),
        }).unwrap();

        toast.success(tToast("blockchainCreatedSuccess"));
        return result as unknown as FilteredResolvedBlockchainBeneficiary;
      } catch (error) {
        createBlockchainGuard.release();
        // Generic translated message — never the raw upstream body (mirrors the
        // fiat createBeneficiary path; see error-handling-ux.md).
        //
        // The bare `catch {` this replaces discarded the error binding on the
        // stated assumption that "the failure is already captured to Sentry by
        // baseQuery" — which holds only for the statuses
        // `captureRtkQueryError` doesn't skip, and not at all for a throw
        // raised while building the payload. Same dead end as the fiat branch,
        // same two-tier report.
        logError("Blockchain recipient creation failed", error, {
          flow: "createBlockchainBeneficiary",
        });
        reportBeneficiaryFailure(error, {
          destination: "blockchain",
          endpointName: "createBlockchainBeneficiary",
        });
        toast.error(tToast("blockchainCreatedError"));
        return null;
      }
    },
    [
      createBlockchainGuard,
      pinnedAccount,
      selectedAssetUuid,
      blockchainRecipientAddress,
      beneficiaryType,
      createBlockchainBeneficiaryMutation,
      tToast,
    ]
  );

  // --- Unsent draft ---------------------------------------------------------
  // Dirty while either details form is dirty, or the entry holds bank details,
  // a travel-rule address or a commit. Protected by the browser's unload
  // prompt and published (as a flag only) for the header's account switcher.
  const [areDetailsDirty, setAreDetailsDirty] = useState(false);
  const [hasCompletedCreate, setHasCompletedCreate] = useState(false);
  const hasEntries =
    Object.keys(bankAccount).length > 0 ||
    Object.values(blockchainRecipientAddress).some(Boolean) ||
    stepMarkers.destinationCommitted ||
    stepMarkers.accountCommitted;
  const isDraftDirty = !hasCompletedCreate && (areDetailsDirty || hasEntries);

  const unloadListenerRef = useRef<((event: BeforeUnloadEvent) => void) | null>(
    null
  );
  const detachUnloadWarning = useCallback(() => {
    if (unloadListenerRef.current) {
      window.removeEventListener("beforeunload", unloadListenerRef.current);
      unloadListenerRef.current = null;
    }
  }, []);
  useEffect(() => {
    if (!isDraftDirty) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome before 119 needs returnValue set to show the prompt.
      event.returnValue = "";
    };
    unloadListenerRef.current = warnBeforeUnload;
    window.addEventListener("beforeunload", warnBeforeUnload);
    return detachUnloadWarning;
  }, [isDraftDirty, detachUnloadWarning]);

  const setDraftDirtyFlag = useRecipientDraftStore((state) => state.setDirty);
  useEffect(() => {
    setDraftDirtyFlag(isDraftDirty);
  }, [isDraftDirty, setDraftDirtyFlag]);
  useEffect(() => () => setDraftDirtyFlag(false), [setDraftDirtyFlag]);

  const completeCreate = useCallback(() => {
    const isOnPinnedAccount =
      pinnedAccountIdRef.current !== null &&
      liveAccountIdRef.current === pinnedAccountIdRef.current;
    if (!isMountedRef.current || !isOnPinnedAccount) return false;
    detachUnloadWarning();
    setHasCompletedCreate(true);
    return true;
  }, [detachUnloadWarning]);

  // --- Whole-entry resets ---------------------------------------------------

  const clearDestinationSelection = useCallback(() => {
    setCurrency("");
    setCountry("");
    setSelectedAsset("");
    setSelectedNetwork(null);
  }, [setSelectedAsset, setSelectedNetwork]);

  const startAgainOnLiveAccount = useCallback(() => {
    restartRecipientEntry();
    clearDestinationSelection();
    setPinnedAccount(
      liveAccountId ? { id: liveAccountId, name: liveAccountName } : null
    );
  }, [
    restartRecipientEntry,
    clearDestinationSelection,
    liveAccountId,
    liveAccountName,
  ]);

  // Reset everything, the Destination selection included.
  const resetForm = useCallback(() => {
    restartRecipientEntry();
    clearDestinationSelection();
    setDestinationType("currencies");
    setBeneficiaryType(null);
  }, [restartRecipientEntry, clearDestinationSelection, setDestinationType]);

  const value: BeneficiaryProviderValue = useMemo(
    () => ({
      destinationType,
      setDestinationType,
      beneficiaryType,
      setBeneficiaryType,
      currency,
      setCurrency,
      country,
      setCountry,
      name,
      setName,
      bankAccount,
      setBankAccount,
      saveBankAccount,
      updateBankAccountField,
      bankAccountFieldError,
      clearBankAccountFieldError,
      // Blockchain state
      selectedAsset,
      setSelectedAsset,
      selectedAssetUuid,
      setSelectedAssetUuid,
      selectedNetwork,
      setSelectedNetwork,
      blockchainRecipientAddress,
      setBlockchainRecipientAddress,
      copCheckResult,
      setCopCheckResult,
      copUserAction,
      setCopUserAction,
      purposeCode,
      setPurposeCode,
      beneficiaryTemplates: beneficiaryTemplates as
        | BeneficiaryTemplate[]
        | undefined,
      selectedTemplate,
      setSelectedTemplate,
      selectedTemplateIndex,
      setSelectedTemplateIndex,
      savedTemplate,
      isFetchingTemplates,
      isErrorTemplates,
      refetchTemplates,
      hasTemplatesForCorridor,
      verifyTemplatesAvailable,
      createBeneficiary,
      createBlockchainBeneficiary,
      isLoadingCreate,
      isLoadingCreateBlockchain,
      completeCreate,
      effectiveStep,
      isActiveStep,
      setCurrentStep,
      commitDestination,
      commitAccount,
      goBackToAccount,
      restartRecipientEntry,
      canProceedToAccountDetails,
      isAccountMismatch,
      pinnedAccountName: pinnedAccount?.name ?? null,
      startAgainOnLiveAccount,
      resetForm,
    }),
    [
      destinationType,
      setDestinationType,
      beneficiaryType,
      currency,
      country,
      name,
      bankAccount,
      saveBankAccount,
      updateBankAccountField,
      bankAccountFieldError,
      clearBankAccountFieldError,
      selectedAsset,
      setSelectedAsset,
      selectedAssetUuid,
      selectedNetwork,
      setSelectedNetwork,
      blockchainRecipientAddress,
      copCheckResult,
      copUserAction,
      purposeCode,
      beneficiaryTemplates,
      selectedTemplate,
      selectedTemplateIndex,
      savedTemplate,
      isFetchingTemplates,
      isErrorTemplates,
      refetchTemplates,
      hasTemplatesForCorridor,
      verifyTemplatesAvailable,
      createBeneficiary,
      createBlockchainBeneficiary,
      isLoadingCreate,
      isLoadingCreateBlockchain,
      completeCreate,
      effectiveStep,
      isActiveStep,
      setCurrentStep,
      commitDestination,
      commitAccount,
      goBackToAccount,
      restartRecipientEntry,
      canProceedToAccountDetails,
      isAccountMismatch,
      pinnedAccount,
      startAgainOnLiveAccount,
      resetForm,
    ]
  );

  return (
    <BeneficiaryContext.Provider value={value}>
      <DetailsDirtyWatcher onChange={setAreDetailsDirty} />
      {children}
    </BeneficiaryContext.Provider>
  );
}

export function useBeneficiary() {
  const context = useContext(BeneficiaryContext);
  if (context === undefined) {
    throw new Error("useBeneficiary must be used within a BeneficiaryProvider");
  }
  return context;
}
